import React, { useState, useEffect } from 'react';
import { UserPlus, Brain, Settings, ChevronRight, Trash2, X, Clock, Users2, Calendar, Phone, Copy, Check, Link } from 'lucide-react';
import QuickCreateDoubleModal from '../components/QuickCreateDoubleModal';
import DeactivateDoubleModal from '../components/DeactivateDoubleModal';
import DoubleSettingsModal from '../components/DoubleSettingsModal';
import VoiceSelect from '../components/VoiceSelect';
import { supabase } from '../utils/supabase';
import { api } from '../utils/api';

interface Double {
  id: string;
  name: string;
  status: 'active' | 'training' | 'inactive';
  personality: string | null;
  knowledge_base: string | null;
  prompt: string | null;
  created_at: string;
  updated_at: string;
  elevenlabs_agent_id: string | null;
  voice_type: string | null;
  voice_id?: string | null;
  voice_name?: string | null;
  first_message: string | null;
  phone_number?: string | null;
  status_changed_at?: string | null;
}

interface TrainingModalProps {
  double: Double;
  onClose: () => void;
  onSubmit: (id: string, data: any) => void;
}

const MyDoubles = () => {
  const [doubles, setDoubles] = useState<Double[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTrainingModal, setShowTrainingModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [selectedDouble, setSelectedDouble] = useState<string | null>(null);
  const [showActivityModal, setShowActivityModal] = useState(false);

  useEffect(() => {
    const fetchDoubles = async () => {
      try {
        const { data, error } = await supabase
          .from('doubles')
          .select('*')
          .order('created_at', { ascending: false });

        if (error) throw error;
        
        setDoubles(data || []);
      } catch (err) {
        console.error('Error fetching doubles:', err);
        setError('Failed to load doubles');
      } finally {
        setLoading(false);
      }
    };

    fetchDoubles();
  }, []);

  const handleCreateDouble = async (formData: {
    name: string;
    purpose: string;
    voice_id: string;
    voice_name: string | null;
    knowledgeBaseType: 'create' | 'assist';
    first_message: string;
    prompt: string;
    knowledge_base: string;
  }) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData.user?.id || null;

      const response = await api.post<{ double: Double }>(`/doubles`, {
        name: formData.name,
        personality: formData.purpose,
        knowledge_base: formData.knowledge_base,
        prompt: formData.prompt,
        first_message: formData.first_message,
        voice_id: formData.voice_id || null,
        voice_name: formData.voice_name || null,
        user_id: userId,
      });

      if (response?.double) {
        setDoubles(prev => [response.double, ...prev]);
      }
      
    } catch (error) {
      console.error('Error creating double:', error);
      setError('Failed to create double');
    }
  };

  const handleTrainingUpdate = async (id: string, data: any) => {
    try {
      const response = await api.put<{ double: Double }>(`/doubles/${id}`, {
        ...data,
        status: 'active',
      });

      if (response?.double) {
        const updatedDoubles = doubles.map(double => 
          double.id === id ? response.double : double
        );
        setDoubles(updatedDoubles);
      }
    } catch (error) {
      console.error('Error updating double:', error);
      setError('Failed to update double');
    }
  };

  const handleDeactivate = async (id: string) => {
    try {
      const updatedDoubles = doubles.map(double => 
        double.id === id ? { ...double, status: 'inactive' } : double
      );
      setDoubles(updatedDoubles);
    } catch (error) {
      console.error('Error deactivating double:', error);
      setError('Failed to deactivate double');
    }
  };

  const handleSettingsUpdate = async (id: string, data: any) => {
    const updatedDoubles = doubles.map(double => 
      double.id === id 
        ? { ...double, ...data } 
        : double
    );
    setDoubles(updatedDoubles);
  };

  // Separate doubles by status
  const activeDoubles = doubles.filter(d => d.status === 'active');
  const trainingDoubles = doubles.filter(d => d.status === 'training');

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
        <p className="text-gray-600">Loading doubles...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
        {error}
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">My Doubles</h1>
          <p className="text-gray-600">Manage your AI doubles and their capabilities.</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="button-primary flex items-center gap-2"
        >
          <UserPlus className="h-5 w-5" />
          Create a Double
        </button>
      </div>
      
      {/* In Training Section */}
      {trainingDoubles.length > 0 && (
        <div className="mb-12">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">In Training</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {trainingDoubles.map((double) => (
              <div
                key={double.id}
                onClick={() => {
                  setSelectedDouble(double.id);
                  setShowTrainingModal(true);
                }}
                className="bg-white p-6 rounded-xl border-2 border-dark shadow-retro hover:shadow-none hover:-translate-y-1 transition-all cursor-pointer"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-yellow-600 font-semibold text-lg">{double.name[0]}</span>
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">{double.name}</h3>
                    <p className="text-gray-600 mt-1">{double.personality}</p>
                    <div className="mt-4">
                      <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm rounded-full">
                        Needs configuration
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Active Doubles Section */}
      <h2 className="text-xl font-semibold text-gray-900 mb-4">In the Field</h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {activeDoubles.map((double) => (
          <div
            key={double.id}
            className="bg-white p-6 rounded-xl border-2 border-dark shadow-retro hover:shadow-none hover:-translate-y-1 transition-all"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <span className="text-primary font-semibold text-lg">{double.name[0]}</span>
              </div>
              
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">{double.name}</h3>
                    <p className="text-gray-600 mt-1">{double.personality}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedDouble(double.id);
                        setShowSettingsModal(true);
                      }}
                      className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Settings"
                    >
                      <Settings className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedDouble(double.id);
                        setShowDeactivateModal(true);
                      }}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Deactivate"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                <div className="mt-4 space-y-4">
                  {double.phone_number && (
                    <div className="flex items-center justify-between p-3 bg-primary/5 rounded-lg">
                      <div className="flex items-center gap-2">
                      <Phone className="h-5 w-5 text-primary" />
                      <span className="font-medium">{double.phone_number}</span>
                      </div>
                      <CopyButton text={double.phone_number} />
                    </div>
                  )}

                  {double.scheduling_link && (
                    <div className="flex items-center justify-between p-3 bg-primary/5 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Link className="h-5 w-5 text-primary" />
                        <span className="font-medium">mycal.live/{double.scheduling_link}</span>
                      </div>
                      <CopyButton text={`https://mycal.live/${double.scheduling_link}`} />
                    </div>
                  )}

                  <button 
                    onClick={() => {
                      setSelectedDouble(double.id);
                      setShowActivityModal(true);
                    }}
                    className="w-full mt-4 flex items-center justify-center gap-2 px-4 py-2 text-primary hover:bg-primary/5 rounded-lg transition-colors"
                  >
                    View Details
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}

        {activeDoubles.length === 0 && (
          <div className="lg:col-span-2 text-center py-12 bg-gray-100 rounded-lg border-2 border-dashed border-gray-200">
            <p className="text-gray-600">No active doubles yet. Complete the training for your doubles to see them here.</p>
          </div>
        )}
      </div>

      {showCreateModal && (
        <QuickCreateDoubleModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateDouble}
        />
      )}
      
      {showTrainingModal && selectedDouble && (
        <TrainingModal
          double={doubles.find(d => d.id === selectedDouble)!}
          onClose={() => {
            setShowTrainingModal(false);
            setSelectedDouble(null);
          }}
          onSubmit={handleTrainingUpdate}
        />
      )}
      
      {showSettingsModal && selectedDouble && (
        <DoubleSettingsModal
          double={doubles.find(d => d.id === selectedDouble)!}
          onClose={() => {
            setShowSettingsModal(false);
            setSelectedDouble(null);
          }}
          onUpdate={handleSettingsUpdate}
        />
      )}
      
      {showDeactivateModal && selectedDouble && (
        <DeactivateDoubleModal
          double={doubles.find(d => d.id === selectedDouble)!}
          onClose={() => {
            setShowDeactivateModal(false);
            setSelectedDouble(null);
          }}
          onDeactivate={handleDeactivate}
        />
      )}
    </div>
  );
};

const CopyButton = ({ text }: { text: string }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className="p-2 text-gray-600 hover:bg-white rounded-lg transition-colors"
      title={copied ? 'Copied!' : 'Copy to clipboard'}
    >
      {copied ? (
        <Check className="h-4 w-4 text-green-500" />
      ) : (
        <Copy className="h-4 w-4" />
      )}
    </button>
  );
};

export default MyDoubles;

const TrainingModal = ({ double, onClose, onSubmit }: TrainingModalProps) => {
  const [formData, setFormData] = useState({
    first_message: double.first_message || '',
    knowledge_base: double.knowledge_base || '',
    prompt: double.prompt || '',
    voice_id: double.voice_id || double.voice_type || '',
    voice_name: double.voice_name || null,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      onSubmit(double.id, {
        first_message: formData.first_message,
        knowledge_base: formData.knowledge_base,
        prompt: formData.prompt,
        voice_id: formData.voice_id || null,
        voice_name: formData.voice_name || null,
        status: 'active',
      });
      onClose();
    } catch (error) {
      console.error('Error updating double:', error);
      // Handle error appropriately in your UI
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      style={{ overflowY: 'auto' }}
    >
      <div 
        className="bg-white w-full max-w-2xl rounded-xl shadow-retro border-2 border-dark my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Configure {double.name}</h2>
            <p className="text-gray-600 mt-1">Complete the training by providing knowledge and instructions</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[calc(100vh-16rem)] overflow-y-auto">
          <div>
            <label htmlFor="firstMessage" className="block text-sm font-medium text-gray-700 mb-1">
              First Message
            </label>
            <textarea
              id="firstMessage"
              value={formData.first_message}
              onChange={(e) => setFormData(prev => ({ ...prev, first_message: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary h-24"
              placeholder="Enter the first message your double will say when starting a conversation..."
            />
            <p className="mt-1 text-sm text-gray-500">
              This message will be used to start every conversation with your double.
            </p>
          </div>

          <div>
            <label htmlFor="knowledgeBase" className="block text-sm font-medium text-gray-700 mb-1">
              Knowledge Base
            </label>
            <textarea
              id="knowledgeBase"
              required
              value={formData.knowledge_base}
              onChange={(e) => setFormData(prev => ({ ...prev, knowledge_base: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary h-40"
              placeholder="Enter the knowledge base for your double..."
            />
          </div>

          <VoiceSelect
            value={formData.voice_id}
            onChange={(voiceId) => setFormData(prev => ({ ...prev, voice_id: voiceId }))}
            onSelect={(voice) => setFormData(prev => ({ ...prev, voice_name: voice.name }))}
            label="Voice"
            helpText="Choose the voice your double will use."
          />

          <div>
            <label htmlFor="prompt" className="block text-sm font-medium text-gray-700 mb-1">
              Prompt
              <span className="ml-1 text-gray-500">(System Instructions)</span>
            </label>
            <textarea
              id="prompt"
              required
              value={formData.prompt}
              onChange={(e) => setFormData(prev => ({ ...prev, prompt: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary h-40"
              placeholder="Enter the prompt instructions for your double..."
            />
          </div>
        </form>
        <div className="p-6 border-t border-gray-200 bg-white sticky bottom-0">
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!formData.knowledge_base.trim() || !formData.prompt.trim()}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              Complete Training
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
